import { px } from '@adsk/alloy-react-helpers';

import {
  AlloyLegacyThemeDesignTokens,
  TileBorderStyle,
  TileDesignTokens,
} from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;
type RawTileTokens = NonNullable<WeaveTokens['component']>['tile'];

const TILE_BORDER_STYLES: ReadonlySet<string> = new Set([
  'none',
  'hidden',
  'dotted',
  'dashed',
  'solid',
  'double',
  'groove',
  'ridge',
  'inset',
  'outset',
]);

const toTileBorderStyle = (
  value: string | undefined,
): TileBorderStyle | undefined => {
  if (value === undefined || !TILE_BORDER_STYLES.has(value)) {
    return undefined;
  }

  return value as TileBorderStyle;
};

type BoxShadowParts = {
  x?: string;
  y?: string;
  blur?: string;
  spread?: string;
  color?: string;
};

const composeBoxShadow = ({
  x,
  y,
  blur,
  spread,
  color,
}: BoxShadowParts): string | undefined => {
  if (!color) {
    return undefined;
  }

  return `${x ?? '0px'} ${y ?? '0px'} ${blur ?? '0px'} ${spread ?? '0px'} ${color}`;
};

const getElevatedShadow = (
  tile: RawTileTokens | undefined,
  state: 'default' | 'hover' | 'pressed',
) =>
  composeBoxShadow({
    x: tile?.elevated?.['box-shadow-x']?.[state],
    y: tile?.elevated?.['box-shadow-y']?.[state],
    blur: tile?.elevated?.['box-shadow-blur']?.[state],
    spread: tile?.elevated?.['box-shadow-spread']?.[state],
    color: tile?.elevated?.['box-shadow-color']?.[state],
  });

const getTileTransition = (
  tile: RawTileTokens | undefined,
): string | undefined => {
  const duration = tile?.['transition-duration'];
  const timing = tile?.['transition-timing-function'];

  if (!duration || !timing) {
    return undefined;
  }

  return `background-color ${duration} ${timing}, box-shadow ${duration} ${timing}, opacity ${duration} ${timing}`;
};

export const getTileTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
  fontFamily: string,
): TileDesignTokens => {
  const bodySmallBold = theme.typography.bodySmallBold;
  const bodySmall = theme.typography.bodySmall;
  const labelMedium = theme.typography.labelMedium;
  const textPrimary = theme.tokens.colors.text.primary.value;
  const borderSubtle = theme.tokens.colors.border.subtle.value;
  const borderDefault = theme.tokens.colors.border.value;
  const borderSelected = theme.tokens.colors.border.selected.value;

  return {
    container: {
      _: {
        'border-radius': px(theme.borderRadius.medium),
        'box-shadow': theme.shadows.lowElevation,
        'background-color': undefined,
        'border-width': undefined,
        'border-style': undefined,
        'border-color': undefined,
        transition: undefined,
      },
      hover: {
        'box-shadow': theme.halos.hover,
        'background-color': undefined,
      },
      selected: {
        'box-shadow': `0 0 0 4px ${theme.colors.focusHalo}`,
        'background-color': undefined,
        'border-width': undefined,
        'border-style': undefined,
        'border-color': undefined,
      },
      'selected-hover': {
        'box-shadow': theme.halos.hover,
        'background-color': undefined,
      },
    },
    image: {
      'background-color': theme.tokens.colors.background.neutral.inactive.value,
      'border-width': px(1),
      'border-style': 'solid',
      'border-color': {
        default: borderSubtle,
        hover: borderDefault,
        selected: borderSelected,
      },
      'border-top-left-radius': px(theme.borderRadius.medium),
      'border-top-right-radius': px(theme.borderRadius.medium),
      'border-bottom-left-radius': px(theme.borderRadius.none),
      'border-bottom-right-radius': px(theme.borderRadius.none),
      'placeholder-icon-color': theme.tokens.colors.icon.inactive.value,
    },
    'content-container': {
      'padding-top': px(theme.spacing.XS),
      'padding-right': px(theme.spacing.XS),
      'padding-bottom': px(theme.spacing.XS),
      'padding-left': px(theme.spacing.XS),
      'border-width': px(1),
      'border-style': 'solid',
      'border-color': {
        default: borderSubtle,
        hover: borderDefault,
        selected: borderSelected,
      },
      'border-bottom-left-radius': px(3),
      'border-bottom-right-radius': px(3),
    },
    title: {
      'padding-top': undefined,
      'padding-right': undefined,
      'padding-bottom': undefined,
      'padding-left': undefined,
      'min-height': px(16),
      color: textPrimary,
      'font-family': fontFamily,
      'font-size': bodySmallBold.fontSize,
      'font-weight': String(bodySmallBold.fontWeight),
      'line-height': bodySmallBold.lineHeight,
    },
    subtitle: {
      'margin-top': px(6),
      'padding-top': undefined,
      'padding-right': undefined,
      'padding-bottom': undefined,
      'padding-left': undefined,
      color: textPrimary,
      'font-family': fontFamily,
      'font-size': bodySmall.fontSize,
      'font-weight': String(bodySmall.fontWeight),
      'line-height': bodySmall.lineHeight,
    },
    metadata: {
      'margin-top': px(theme.spacing.XS),
      'padding-top': undefined,
      'padding-right': undefined,
      'padding-bottom': undefined,
      'padding-left': undefined,
      gap: undefined,
      color: textPrimary,
      'font-family': fontFamily,
      'font-size': bodySmall.fontSize,
      'font-weight': String(bodySmall.fontWeight),
      'line-height': bodySmall.lineHeight,
    },
    overlay: {
      'padding-top': px(6),
      'padding-right': px(6),
      'padding-bottom': px(0),
      'padding-left': px(6),
    },
    badge: {
      'background-color': theme.tokens.colors.background.accent.six.bold.value,
      color: theme.tokens.colors.text.inverse.value,
      'border-radius': px(theme.borderRadius.pill),
      'padding-top': px(theme.spacing.XXXS),
      'padding-right': px(theme.spacing.XS),
      'padding-bottom': px(theme.spacing.XXXS),
      'padding-left': px(theme.spacing.XS),
      'font-family': fontFamily,
      'font-size': labelMedium.fontSize,
      'font-weight': String(labelMedium.fontWeight),
      'line-height': labelMedium.lineHeight,
    },
  };
};

export const getTileTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): TileDesignTokens => {
  const tile = tokens?.component?.tile;
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;
  const textColor = semantic?.['text-color']?.medium?.default;
  const spacing8 = generic?.spacing?.[8];

  return {
    container: {
      _: {
        'border-radius': tile?.['border-radius'],
        'box-shadow': getElevatedShadow(tile, 'default'),
        'background-color':
          tile?.['surface-high']?.['background-color']?.default,
        'border-width': tile?.['border-width']?.default,
        'border-style': toTileBorderStyle(tile?.['border-style']?.default),
        'border-color': tile?.['border-color']?.default,
        transition: getTileTransition(tile),
      },
      hover: {
        'box-shadow': getElevatedShadow(tile, 'hover'),
        'background-color': tile?.['surface-high']?.['background-color']?.hover,
      },
      selected: {
        'box-shadow': tile?.['box-shadow']?.focus,
        'background-color': tile?.['background-color']?.selected?.default,
        'border-width': tile?.['border-width']?.selected,
        'border-style': toTileBorderStyle(tile?.['border-style']?.selected),
        'border-color': tile?.['border-color']?.selected,
      },
      'selected-hover': {
        'box-shadow': undefined,
        'background-color': tile?.['background-color']?.selected?.hover,
      },
    },
    image: {
      'background-color': semantic?.['background-color']?.surface?.[250],
      'border-width': '0px',
      'border-style': toTileBorderStyle(tile?.['border-style']?.default),
      'border-color': {
        default: tile?.['border-color']?.default,
        hover: tile?.['border-color']?.default,
        selected: tile?.['border-color']?.selected,
      },
      'border-top-left-radius': tile?.['border-radius'],
      'border-top-right-radius': tile?.['border-radius'],
      'border-bottom-left-radius': tile?.['border-radius'],
      'border-bottom-right-radius': tile?.['border-radius'],
      'placeholder-icon-color': semantic?.['icon-color']?.medium,
    },
    'content-container': {
      'padding-top': undefined,
      'padding-right': undefined,
      'padding-bottom': undefined,
      'padding-left': undefined,
      'border-width': '0px',
      'border-style': toTileBorderStyle(tile?.['border-style']?.default),
      'border-color': {
        default: tile?.['border-color']?.default,
        hover: tile?.['border-color']?.default,
        selected: tile?.['border-color']?.selected,
      },
      'border-bottom-left-radius': tile?.['border-radius'],
      'border-bottom-right-radius': tile?.['border-radius'],
    },
    title: {
      'padding-top': tile?.title?.['padding-top'],
      'padding-right': tile?.['content-container']?.['padding-right'],
      'padding-bottom': tile?.title?.['padding-bottom'],
      'padding-left': tile?.['content-container']?.['padding-left'],
      'min-height': generic?.['line-height']?.[20],
      color: textColor,
      'font-family': fontFamily,
      'font-size': generic?.['font-size']?.[14],
      'font-weight': generic?.['font-weight']?.bold,
      'line-height': generic?.['line-height']?.[20],
    },
    subtitle: {
      'margin-top': undefined,
      'padding-top': tile?.['sub-title']?.['padding-top'],
      'padding-right': tile?.['content-container']?.['padding-right'],
      'padding-bottom': tile?.['sub-title']?.['padding-bottom'],
      'padding-left': tile?.['content-container']?.['padding-left'],
      color: textColor,
      'font-family': fontFamily,
      'font-size': generic?.['font-size']?.[12],
      'font-weight': generic?.['font-weight']?.regular,
      'line-height': generic?.['line-height']?.[18],
    },
    metadata: {
      'margin-top': undefined,
      'padding-top':
        tile?.['orientation-vertical']?.['optional-content-container']?.[
          'padding-top'
        ],
      'padding-right':
        tile?.['orientation-vertical']?.['optional-content-container']?.[
          'padding-right'
        ],
      'padding-bottom':
        tile?.['orientation-vertical']?.['optional-content-container']?.[
          'padding-bottom'
        ],
      'padding-left':
        tile?.['orientation-vertical']?.['optional-content-container']?.[
          'padding-left'
        ],
      gap: generic?.spacing?.[4],
      color: textColor,
      'font-family': fontFamily,
      'font-size': generic?.['font-size']?.[12],
      'font-weight': generic?.['font-weight']?.regular,
      'line-height': generic?.['line-height']?.[18],
    },
    overlay: {
      'padding-top': spacing8,
      'padding-right': spacing8,
      'padding-bottom': spacing8,
      'padding-left': spacing8,
    },
    badge: {
      'background-color': semantic?.['background-color']?.accent,
      color: semantic?.['text-color']?.['on-accent'],
      'border-radius': generic?.['border-radius']?.pill,
      'padding-top': generic?.spacing?.[2],
      'padding-right': spacing8,
      'padding-bottom': generic?.spacing?.[2],
      'padding-left': spacing8,
      'font-family': fontFamily,
      'font-size': generic?.['font-size']?.[14],
      'font-weight': generic?.['font-weight']?.medium,
      'line-height': generic?.['line-height']?.[20],
    },
  };
};
